/**
 * Combat Test Scenarios
 * Predefined enemy configurations for the combat simulator.
 * Port of server/src/lib/game-logic/combat-test-scenarios.ts
 */

import type { EnemyDefinition, PlayerDefinition } from './types';
import type { MapTemplate, PositionId } from './grid';

export interface CombatTestScenario {
  id: string;
  name: string;
  description: string;
  category: 'basic' | 'group' | 'boss' | 'stress';
  enemies: EnemyDefinition[];
  mapTemplates: MapTemplate[];  // available maps (first = default)
  sceneMaps?: string[];  // available scene maps (terrain presets)
}

// ============================================================================
// ADVENTURER TEMPLATES
// ============================================================================

export type AdventurerKit = 'blade' | 'shadow' | 'sage' | 'warden' | 'wanderer';

export interface AdventurerTemplate extends PlayerDefinition {
  id: string;
  kit: AdventurerKit;
  ancestry: string;
  origin: string;
  description: string;
}

const KIT_LABELS: Record<AdventurerKit, string> = {
  blade: 'Blade',
  shadow: 'Shadow',
  sage: 'Sage',
  warden: 'Warden',
  wanderer: 'Wanderer',
};

export { KIT_LABELS };

export const ADVENTURER_TEMPLATES: AdventurerTemplate[] = [
  // --- BLADE kit: martial fighters ---
  {
    id: 'kael_blade',
    kit: 'blade',
    name: 'Kael',
    ancestry: 'Human (Pure)',
    origin: 'Watch Recruit',
    description: 'A disciplined swordsman trained by the Steel Watch',
    vitals: 10,
    vitalsMax: 10,
    resolve: 8,
    resolveMax: 8,
    stats: { brawn: 4, grace: 3, wits: 1, spirit: 2 },
    tags: ['Adrenaline', 'Disciplined'],
    equipment: ['Sword', 'Buckler', 'Leather Armor'],
    magicDiscipline: 'kinetic',
    skillIds: ['whirlwind', 'feint'],
  },
  {
    id: 'thira_blade',
    kit: 'blade',
    name: 'Thira',
    ancestry: 'Halfling (Dwarven Blood)',
    origin: 'Watch Recruit',
    description: 'A stocky halfling with dwarven grit and a heavy blade',
    vitals: 10,
    vitalsMax: 10,
    resolve: 8,
    resolveMax: 8,
    stats: { brawn: 3, grace: 2, wits: 2, spirit: 3 },
    tags: ['Slippery', 'Stoneblooded', 'Disciplined', 'armored'],
    equipment: ['Sword', 'Round Shield', 'Chain Shirt'],
    magicDiscipline: 'none',
    skillIds: ['shield_bash', 'shield_wall'],
  },

  // --- SHADOW kit: stealth / precision ---
  {
    id: 'vex_shadow',
    kit: 'shadow',
    name: 'Vex',
    ancestry: 'Halfling (Elven Blood)',
    origin: 'Drifter',
    description: 'A quick-fingered layerless rogue touched by the Veil',
    vitals: 8,
    vitalsMax: 8,
    resolve: 10,
    resolveMax: 10,
    stats: { brawn: 1, grace: 4, wits: 3, spirit: 2 },
    tags: ['Slippery', 'Veil Touched', 'Tenacious'],
    equipment: ['Dagger', 'Masterwork Lockpicks', 'Leather Armor'],
    magicDiscipline: 'veil',
    skillIds: ['nimble_strike', 'disengage'],
  },
  {
    id: 'renn_shadow',
    kit: 'shadow',
    name: 'Renn',
    ancestry: 'Human (Pure)',
    origin: 'Drifter',
    description: 'A street-hardened knife fighter who trusts no one',
    vitals: 9,
    vitalsMax: 9,
    resolve: 8,
    resolveMax: 8,
    stats: { brawn: 2, grace: 4, wits: 3, spirit: 1 },
    tags: ['Adrenaline', 'Tenacious'],
    equipment: ['Dagger', 'Dagger', 'Leather Armor'],
    magicDiscipline: 'none',
    skillIds: ['nimble_strike', 'feint'],
  },

  // --- SAGE kit: knowledge / magic ---
  {
    id: 'lystra_sage',
    kit: 'sage',
    name: 'Lystra',
    ancestry: 'Human (Elven Blood)',
    origin: 'Spire Scribe',
    description: 'An elven-blooded scholar from the Athenaeum who channels the Influence glyph',
    vitals: 8,
    vitalsMax: 8,
    resolve: 12,
    resolveMax: 12,
    stats: { brawn: 1, grace: 2, wits: 4, spirit: 3 },
    tags: ['Adrenaline', 'Veil Touched', 'Learned'],
    equipment: ['Knife', 'Crystal Orb', 'Padded Armor'],
    magicDiscipline: 'influence',
    skillIds: ['battle_cry', 'feint'],
  },
  {
    id: 'dorin_sage',
    kit: 'sage',
    name: 'Dorin',
    ancestry: 'Human (Dilute)',
    origin: 'Spire Scribe',
    description: 'A quiet archivist with a spark of latent magic',
    vitals: 8,
    vitalsMax: 8,
    resolve: 12,
    resolveMax: 12,
    stats: { brawn: 1, grace: 1, wits: 4, spirit: 4 },
    tags: ['Adrenaline', 'Learned'],
    equipment: ['Knife', 'Crystal Orb', 'Padded Armor'],
    magicDiscipline: 'ember',
    skillIds: ['rallying_shout'],
  },

  // --- WARDEN kit: protector / tank ---
  {
    id: 'borin_warden',
    kit: 'warden',
    name: 'Borin',
    ancestry: 'Human (Dwarven Blood)',
    origin: 'Dockhand',
    description: 'A broad-shouldered dockworker with dwarven endurance',
    vitals: 12,
    vitalsMax: 12,
    resolve: 8,
    resolveMax: 8,
    stats: { brawn: 4, grace: 1, wits: 2, spirit: 3 },
    tags: ['Adrenaline', 'Stoneblooded', 'Hardy', 'armored'],
    equipment: ['Mace', 'Round Shield', 'Chain Shirt'],
    magicDiscipline: 'vitality',
    skillIds: ['shield_wall', 'taunt'],
  },
  {
    id: 'marta_warden',
    kit: 'warden',
    name: 'Marta',
    ancestry: 'Human (Pure)',
    origin: 'Dockhand',
    description: 'A tough Roots hauler who picks up the shield when trouble comes',
    vitals: 12,
    vitalsMax: 12,
    resolve: 6,
    resolveMax: 6,
    stats: { brawn: 4, grace: 2, wits: 1, spirit: 3 },
    tags: ['Adrenaline', 'Hardy', 'armored'],
    equipment: ['Mace', 'Round Shield', 'Leather Armor'],
    magicDiscipline: 'none',
    skillIds: ['shield_bash', 'second_wind'],
  },

  // --- WANDERER kit: versatile / ranged ---
  {
    id: 'asha_wanderer',
    kit: 'wanderer',
    name: 'Asha',
    ancestry: 'Halfling (Pure)',
    origin: 'Lamplighter',
    description: 'A sharp-eyed halfling lamplighter with a short bow',
    vitals: 8,
    vitalsMax: 8,
    resolve: 10,
    resolveMax: 10,
    stats: { brawn: 1, grace: 3, wits: 3, spirit: 3 },
    tags: ['Slippery', 'Vigilant'],
    equipment: ['Short Bow', 'Leather Armor'],
    magicDiscipline: 'ember',
    skillIds: ['disengage'],
  },
  {
    id: 'fenris_wanderer',
    kit: 'wanderer',
    name: 'Fenris',
    ancestry: 'Human (Elven Blood)',
    origin: 'Drifter',
    description: 'A restless layerless scout who slips between the cracks of Vannar',
    vitals: 9,
    vitalsMax: 9,
    resolve: 10,
    resolveMax: 10,
    stats: { brawn: 2, grace: 3, wits: 2, spirit: 3 },
    tags: ['Adrenaline', 'Veil Touched', 'Tenacious'],
    equipment: ['Short Bow', 'Dagger', 'Leather Armor'],
    magicDiscipline: 'veil',
    skillIds: ['nimble_strike', 'disengage'],
  },
];

export function getTemplatesByKit(): Record<AdventurerKit, AdventurerTemplate[]> {
  const grouped: Record<AdventurerKit, AdventurerTemplate[]> = {
    blade: [], shadow: [], sage: [], warden: [], wanderer: [],
  };
  for (const t of ADVENTURER_TEMPLATES) {
    grouped[t.kit].push(t);
  }
  return grouped;
}

export function getTemplateById(id: string): AdventurerTemplate | undefined {
  return ADVENTURER_TEMPLATES.find(t => t.id === id);
}

// ============================================================================
// DEFAULT PLAYER (backward compat)
// ============================================================================

export const DEFAULT_PLAYER: PlayerDefinition = {
  name: 'Adventurer',
  vitals: 12,
  vitalsMax: 12,
  resolve: 8,
  resolveMax: 8,
  stats: { brawn: 3, grace: 3, wits: 2, spirit: 2 },
};

export const COMBAT_TEST_SCENARIOS: CombatTestScenario[] = [
  // ============================================================================
  // BASIC (1v1) SCENARIOS
  // ============================================================================
  {
    id: 'single_bandit',
    name: 'Single Bandit',
    description: 'One basic melee enemy - tests simple combat flow',
    category: 'basic',
    mapTemplates: ['open_field', 'wide_area', 'small_room'],
    sceneMaps: ['forest_clearing', 'ruined_courtyard'],
    enemies: [
      {
        name: 'Desperate Bandit',
        description: 'A ragged man with a rusty blade, driven by hunger',
        vitals: 6,
        vitalsMax: 6,
        resolve: 4,
        resolveMax: 4,
        tags: ['aggressive', 'weak'],
        startPosition: 'N3',
      },
    ],
  },
  {
    id: 'skilled_duelist',
    name: 'Skilled Duelist',
    description: 'One tactical enemy with good stats - tests reactions and parry',
    category: 'basic',
    mapTemplates: ['open_field', 'wide_area', 'small_room'],
    sceneMaps: ['ruined_courtyard', 'narrow_bridge'],
    enemies: [
      {
        name: 'Wandering Duelist',
        description: 'A scarred warrior seeking a worthy opponent',
        vitals: 10,
        vitalsMax: 10,
        resolve: 8,
        resolveMax: 8,
        tags: ['tactical', 'agile', 'riposte'],
        startPosition: 'E3',
      },
    ],
  },
  {
    id: 'archer_scout',
    name: 'Archer Scout',
    description: 'One ranged enemy starting far - tests positioning and movement',
    category: 'basic',
    mapTemplates: ['open_field', 'wide_area'],
    sceneMaps: ['forest_clearing', 'riverside_camp'],
    enemies: [
      {
        name: 'Forest Scout',
        description: 'A keen-eyed archer lurking at distance',
        vitals: 5,
        vitalsMax: 5,
        resolve: 6,
        resolveMax: 6,
        tags: ['cunning', 'ranged'],
        startPosition: 'S3',
      },
    ],
  },

  // ============================================================================
  // GROUP COMBAT SCENARIOS
  // ============================================================================
  {
    id: 'bandit_gang',
    name: 'Bandit Gang (3)',
    description: 'Three mixed enemies - tests multi-target combat and targeting',
    category: 'group',
    mapTemplates: ['open_field', 'wide_area'],
    sceneMaps: ['forest_clearing', 'ruined_courtyard'],
    enemies: [
      {
        name: 'Bandit Thug',
        description: 'A brutish enforcer with a heavy club',
        vitals: 6,
        vitalsMax: 6,
        resolve: 3,
        resolveMax: 3,
        tags: ['aggressive', 'strong'],
        startPosition: 'N1',
      },
      {
        name: 'Bandit Archer',
        description: 'A wiry figure with a short bow',
        vitals: 4,
        vitalsMax: 4,
        resolve: 4,
        resolveMax: 4,
        tags: ['cunning', 'ranged'],
        startPosition: 'SE2',
      },
      {
        name: 'Bandit Cutthroat',
        description: 'A quick blade in the shadows',
        vitals: 5,
        vitalsMax: 5,
        resolve: 4,
        resolveMax: 4,
        tags: ['agile', 'quick'],
        startPosition: 'W1',
      },
    ],
  },
  {
    id: 'wolf_pack',
    name: 'Wolf Pack (4)',
    description: 'Four fast, weak enemies - tests action economy and AP management',
    category: 'group',
    mapTemplates: ['open_field', 'wide_area'],
    sceneMaps: ['forest_clearing', 'riverside_camp'],
    enemies: [
      {
        name: 'Alpha Wolf',
        description: 'The pack leader, larger and fiercer than the rest',
        vitals: 8,
        vitalsMax: 8,
        resolve: 6,
        resolveMax: 6,
        tags: ['aggressive', 'quick', 'pack_tactics'],
        startPosition: 'N1',
      },
      {
        name: 'Grey Wolf',
        description: 'A snarling hunter',
        vitals: 4,
        vitalsMax: 4,
        resolve: 3,
        resolveMax: 3,
        tags: ['quick', 'weak'],
        startPosition: 'E1',
      },
      {
        name: 'Grey Wolf',
        description: 'A snarling hunter',
        vitals: 4,
        vitalsMax: 4,
        resolve: 3,
        resolveMax: 3,
        tags: ['quick', 'weak'],
        startPosition: 'SW1',
      },
      {
        name: 'Young Wolf',
        description: 'A smaller but eager predator',
        vitals: 3,
        vitalsMax: 3,
        resolve: 2,
        resolveMax: 2,
        tags: ['quick', 'weak'],
        startPosition: 'S2',
      },
    ],
  },

  // ============================================================================
  // BOSS SCENARIOS
  // ============================================================================
  {
    id: 'cave_troll',
    name: 'Cave Troll',
    description: 'One powerful brute with high vitals - tests sustained damage over rounds',
    category: 'boss',
    mapTemplates: ['wide_area', 'open_field'],
    enemies: [
      {
        name: 'Cave Troll',
        description: 'A massive creature of stone and rage, its hide thick as armor',
        vitals: 20,
        vitalsMax: 20,
        resolve: 10,
        resolveMax: 10,
        tags: ['boss', 'brute', 'strong', 'heavy', 'armored', 'regenerating'],
        startPosition: 'N2',
      },
    ],
  },
  {
    id: 'cultist_leader',
    name: 'Cultist Leader + Guards',
    description: 'Boss with two adds - tests priority targeting and threat management',
    category: 'boss',
    mapTemplates: ['wide_area', 'open_field'],
    sceneMaps: ['ruined_courtyard', 'burning_hall'],
    enemies: [
      {
        name: 'Cultist Magister',
        description: 'A robed figure crackling with dark energy',
        vitals: 12,
        vitalsMax: 12,
        resolve: 10,
        resolveMax: 10,
        tags: ['boss', 'magical', 'cunning', 'spellcaster'],
        startPosition: 'N2',
      },
      {
        name: 'Cultist Guardian',
        description: 'A devoted protector in heavy robes',
        vitals: 8,
        vitalsMax: 8,
        resolve: 6,
        resolveMax: 6,
        tags: ['defensive', 'guardian', 'steadfast', 'armored'],
        startPosition: 'NE1',
      },
      {
        name: 'Cultist Guardian',
        description: 'A devoted protector in heavy robes',
        vitals: 8,
        vitalsMax: 8,
        resolve: 6,
        resolveMax: 6,
        tags: ['defensive', 'guardian', 'steadfast', 'armored'],
        startPosition: 'NW1',
      },
    ],
  },
  {
    id: 'shadow_knight',
    name: 'Shadow Knight',
    description: 'Single elite enemy with multiple combat tags - tests complex AI behavior',
    category: 'boss',
    mapTemplates: ['wide_area', 'open_field', 'small_room'],
    sceneMaps: ['burning_hall', 'ruined_courtyard'],
    enemies: [
      {
        name: 'Shadow Knight',
        description: 'An armored revenant wreathed in darkness',
        vitals: 16,
        vitalsMax: 16,
        resolve: 12,
        resolveMax: 12,
        tags: ['boss', 'tactical', 'heavily_armored', 'riposte', 'fearsome'],
        startPosition: 'N1',
      },
    ],
  },

  // ============================================================================
  // SKILL COMBAT SCENARIOS
  // ============================================================================
  {
    id: 'guardian_pair',
    name: 'Guardian Pair',
    description: 'Two guardians with Shield Wall and Taunt skills - tests defensive skill AI',
    category: 'group',
    mapTemplates: ['small_room', 'wide_area', 'open_field'],
    enemies: [
      {
        name: 'Iron Guardian',
        description: 'A heavily armored sentinel who protects with shield and voice',
        vitals: 10,
        vitalsMax: 10,
        resolve: 8,
        resolveMax: 8,
        tags: ['guardian', 'defensive', 'protector', 'armored'],
        startPosition: 'N1',
      },
      {
        name: 'Shield Warden',
        description: 'A disciplined protector who draws fire away from allies',
        vitals: 10,
        vitalsMax: 10,
        resolve: 8,
        resolveMax: 8,
        tags: ['guardian', 'defensive', 'protector', 'armored'],
        startPosition: 'E1',
      },
    ],
  },
  {
    id: 'berserker_chief',
    name: 'Berserker Chief + Brutes',
    description: 'A berserker with Whirlwind leading brutes with Shield Bash - tests offensive skills',
    category: 'boss',
    mapTemplates: ['open_field', 'wide_area'],
    sceneMaps: ['ruined_courtyard', 'burning_hall'],
    enemies: [
      {
        name: 'Berserker Chief',
        description: 'A massive warrior who spins through foes like a cyclone',
        vitals: 14,
        vitalsMax: 14,
        resolve: 8,
        resolveMax: 8,
        tags: ['berserker', 'aggressive', 'boss', 'strong'],
        startPosition: 'N1',
      },
      {
        name: 'Brute Raider',
        description: 'A heavy-hitting warrior who bashes foes aside',
        vitals: 7,
        vitalsMax: 7,
        resolve: 5,
        resolveMax: 5,
        tags: ['brute', 'aggressive', 'strong'],
        startPosition: 'NE1',
      },
      {
        name: 'Brute Raider',
        description: 'A heavy-hitting warrior who bashes foes aside',
        vitals: 7,
        vitalsMax: 7,
        resolve: 5,
        resolveMax: 5,
        tags: ['brute', 'aggressive', 'strong'],
        startPosition: 'NW2',
      },
    ],
  },
  {
    id: 'tactical_squad',
    name: 'Tactical Squad',
    description: 'A commander with Battle Cry, assassins with Feint/Nimble Strike, and an archer with Disengage',
    category: 'group',
    mapTemplates: ['open_field', 'wide_area'],
    sceneMaps: ['forest_clearing', 'ruined_courtyard'],
    enemies: [
      {
        name: 'War Commander',
        description: 'A seasoned leader who rallies troops with a thunderous cry',
        vitals: 10,
        vitalsMax: 10,
        resolve: 10,
        resolveMax: 10,
        tags: ['commander', 'leader', 'tactical'],
        startPosition: 'N2',
      },
      {
        name: 'Shadow Assassin',
        description: 'A blade-dancer who feints and strikes with lethal grace',
        vitals: 6,
        vitalsMax: 6,
        resolve: 8,
        resolveMax: 8,
        tags: ['assassin', 'cunning', 'agile'],
        startPosition: 'E1',
      },
      {
        name: 'Shadow Assassin',
        description: 'A blade-dancer who feints and strikes with lethal grace',
        vitals: 6,
        vitalsMax: 6,
        resolve: 8,
        resolveMax: 8,
        tags: ['assassin', 'cunning', 'agile'],
        startPosition: 'W1',
      },
      {
        name: 'Retreating Archer',
        description: 'A ranged fighter who disengages whenever enemies get close',
        vitals: 5,
        vitalsMax: 5,
        resolve: 6,
        resolveMax: 6,
        tags: ['archer', 'cunning', 'ranged'],
        startPosition: 'S3',
      },
    ],
  },
  {
    id: 'support_healers',
    name: 'Healer + Tanks',
    description: 'A healer with Second Wind and Rallying Shout supporting two tanks - tests support skills',
    category: 'group',
    mapTemplates: ['wide_area', 'open_field'],
    enemies: [
      {
        name: 'Battle Priest',
        description: 'A holy warrior who heals allies and bolsters their resolve',
        vitals: 8,
        vitalsMax: 8,
        resolve: 12,
        resolveMax: 12,
        tags: ['healer', 'support', 'priest'],
        startPosition: 'N2',
      },
      {
        name: 'Temple Guardian',
        description: 'A devoted protector with iron resolve',
        vitals: 12,
        vitalsMax: 12,
        resolve: 6,
        resolveMax: 6,
        tags: ['guardian', 'defensive', 'strong', 'armored'],
        startPosition: 'NE1',
      },
      {
        name: 'Temple Guardian',
        description: 'A devoted protector with iron resolve',
        vitals: 12,
        vitalsMax: 12,
        resolve: 6,
        resolveMax: 6,
        tags: ['guardian', 'defensive', 'strong', 'armored'],
        startPosition: 'NW1',
      },
    ],
  },

  // ============================================================================
  // STRESS TEST SCENARIOS
  // ============================================================================
  {
    id: 'goblin_horde',
    name: 'Goblin Horde (8)',
    description: 'Many weak enemies - stress test for UI rendering and performance',
    category: 'stress',
    mapTemplates: ['open_field'] as MapTemplate[],
    enemies: (() => {
      // Spread goblins across compass directions
      const positions: PositionId[] = [
        'N1', 'NE1', 'E1', 'SE1', 'S1', 'SW1', 'W1', 'NW1',
      ];
      return Array.from({ length: 8 }, (_, i) => ({
        name: `Goblin ${i + 1}`,
        description: 'A screeching green menace',
        vitals: 3,
        vitalsMax: 3,
        resolve: 2,
        resolveMax: 2,
        tags: ['weak', 'quick'] as string[],
        startPosition: positions[i],
      }));
    })(),
  },
];

export function getScenarioById(id: string): CombatTestScenario | undefined {
  return COMBAT_TEST_SCENARIOS.find(s => s.id === id);
}

export function getScenariosByCategory(): Record<string, CombatTestScenario[]> {
  const grouped: Record<string, CombatTestScenario[]> = {
    basic: [], group: [], boss: [], stress: [],
  };
  for (const scenario of COMBAT_TEST_SCENARIOS) {
    grouped[scenario.category].push(scenario);
  }
  return grouped;
}
